import type { ProviderSummary } from "./api.ts";
import { emptyState, indexRow } from "./components.ts";
import { el, logoImage } from "./dom.ts";
import { icons } from "./icons.ts";
import type { AppContext } from "./views.ts";
import { visibleProviders } from "./visibility.ts";

/** The 15 provider categories, in browsing order. */
const CATEGORY_ORDER: string[] = [
  "paysites",
  "premiumporn",
  "alternativesites",
  "amateur",
  "hosters",
  "camporn",
  "familyporn",
  "jav",
  "hentai",
  "other",
  "extreme",
  "trans",
  "gay",
  "albums",
  "pics",
];

const CATEGORY_TITLES: Record<string, string> = {
  paysites: "Premium networks",
  premiumporn: "Premium and HD",
  alternativesites: "Alternative sites",
  amateur: "Amateur",
  hosters: "Hosters",
  camporn: "Live cams",
  familyporn: "Family",
  jav: "JAV",
  hentai: "Hentai",
  other: "Other",
  extreme: "Extreme",
  trans: "Trans",
  gay: "Gay",
  albums: "Albums",
  pics: "Pictures",
};

/** Readable label for a provider category id, with a fallback for new ones. */
export function categoryTitle(category: string): string {
  const known = CATEGORY_TITLES[category];
  if (known) return known;
  const spaced = category.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function categoriesView(context: AppContext): HTMLElement {
  context = { ...context, providers: visibleProviders(context.providers) };
  const root = el("div", { class: "view view--categories" });
  const verified = new Set(context.preferred);

  const grouped = new Map<string, ProviderSummary[]>();
  for (const provider of context.providers) {
    const bucket = grouped.get(provider.category) ?? [];
    bucket.push(provider);
    grouped.set(provider.category, bucket);
  }

  // Known categories keep their curated order; unknown ones are appended.
  const order: string[] = [];
  for (const id of CATEGORY_ORDER) {
    if (grouped.has(id)) order.push(id);
  }
  for (const id of grouped.keys()) {
    if (!order.includes(id)) order.push(id);
  }

  /** "" means every category. One choice at a time keeps the index readable. */
  let chosen = "";

  const search = el("input", {
    class: "input",
    attrs: {
      type: "search",
      placeholder: "Find a provider",
      "aria-label": "Find a provider by name, id or site",
      autocomplete: "off",
      spellcheck: "false",
    },
  });

  const matches = (provider: ProviderSummary, query: string): boolean =>
    query === "" ||
    provider.name.toLowerCase().includes(query) ||
    provider.id.toLowerCase().includes(query) ||
    provider.siteUrl.toLowerCase().includes(query);

  const query = (): string => search.value.trim().toLowerCase();

  // ---- category tiles ---------------------------------------------------
  const tiles = el("ul", { class: "cat-grid", attrs: { "aria-label": "Categories" } });
  const tileButtons: { id: string; button: HTMLButtonElement; count: HTMLElement }[] = [];

  const makeTile = (id: string, label: string, providers: ProviderSummary[]): HTMLElement => {
    const button = el("button", {
      class: "cat-tile",
      attrs: { type: "button", "aria-pressed": "false" },
    });
    const logos = el("span", { class: "cat-tile-logos", attrs: { "aria-hidden": "true" } });
    for (const provider of providers.slice(0, 5)) {
      logos.append(el("span", { class: "cat-tile-logo" }, logoImage(provider.logo, provider.name)));
    }
    if (providers.length > 5) {
      logos.append(el("span", { class: "cat-tile-more", text: `+${providers.length - 5}` }));
    }
    const count = el("span", { class: "cat-tile-count", text: String(providers.length) });
    button.append(
      el(
        "span",
        { class: "cat-tile-top" },
        el("span", { class: "cat-tile-name", text: label }),
        count,
      ),
      logos,
    );
    button.addEventListener("click", () => {
      chosen = id;
      paint();
      index.scrollIntoView({ block: "nearest", behavior: "smooth" });
    });
    tileButtons.push({ id, button, count });
    return el("li", { class: "cat-cell" }, button);
  };

  tiles.append(
    makeTile("", "All categories", context.providers),
    ...order.map((id) => makeTile(id, categoryTitle(id), grouped.get(id) ?? [])),
  );

  // ---- one index, filtered by the tile and the field --------------------
  const index = el("section", { class: "cat-index", attrs: { "aria-labelledby": "cat-index-title" } });
  const indexTitle = el("h2", { class: "section-title", attrs: { id: "cat-index-title" } });
  const indexMeta = el("span", { class: "section-meta" });
  const rows = el("ul", { class: "index-list" });
  const emptySlot = el("div");
  index.append(
    el("div", { class: "section-head" }, indexTitle, indexMeta),
    rows,
    emptySlot,
  );

  const paint = (): void => {
    const needle = query();
    const scope = chosen === "" ? order : [chosen];

    for (const tile of tileButtons) {
      const providers = tile.id === "" ? context.providers : grouped.get(tile.id) ?? [];
      const hits = providers.filter((provider) => matches(provider, needle)).length;
      tile.count.textContent = String(needle === "" ? providers.length : hits);
      tile.button.setAttribute("aria-pressed", String(tile.id === chosen));
      tile.button.disabled = needle !== "" && hits === 0;
    }

    const providers = scope
      .flatMap((id) => grouped.get(id) ?? [])
      .filter((provider) => matches(provider, needle))
      .sort((left, right) => left.name.localeCompare(right.name));

    indexTitle.textContent = chosen === "" ? "All categories" : categoryTitle(chosen);
    const verifiedCount = providers.filter((provider) => verified.has(provider.id)).length;
    indexMeta.textContent = `${providers.length} ${providers.length === 1 ? "provider" : "providers"}${
      verifiedCount > 0 ? `, ${verifiedCount} verified` : ""
    }`;

    rows.replaceChildren();
    for (const provider of providers) {
      rows.append(
        indexRow({
          title: provider.name,
          state: provider.siteUrl.replace(/^https?:\/\//, ""),
          leading: el("span", { class: "index-logo" }, logoImage(provider.logo, provider.name)),
          href: `#/provider/${provider.id}`,
          onOpen: () => context.navigate(`#/provider/${provider.id}`),
        }),
      );
    }

    emptySlot.replaceChildren();
    if (providers.length === 0) {
      emptySlot.append(
        emptyState({
          title: "Nothing matches that name.",
          body: "Clear the field to read every provider in this category.",
          action: {
            label: "Clear the filter",
            onOpen: () => {
              search.value = "";
              paint();
            },
          },
        }),
      );
    }
  };

  search.addEventListener("input", () => paint());

  root.append(
    el(
      "header",
      { class: "page-head container" },
      el(
        "div",
        { class: "page-head-copy" },
        el("h1", { class: "page-title", text: "Categories" }),
        el("p", {
          class: "page-sub",
          text: `${order.length} categories, ${context.providers.length} providers, one index below`,
        }),
      ),
      el("div", { class: "field field--filter" }, el("span", { class: "field-icon", html: icons.search }), search),
    ),
    el("div", { class: "container" }, tiles, index),
  );

  paint();
  return root;
}
